import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import cartService from 'service/cart';
import { ICartItemResponse } from 'dto/cartDto';
// -------------------------------------------------------------------------------------------
// 장바구니 생성
export interface IUserCartItem {
  user_id: string;
  product_id: number;
  product_variation_id: number;
  product_count: number;
}
export const createCartItem = createAsyncThunk(
  'cart/create',
  async (
    { user_id, product_id, product_variation_id, product_count }: IUserCartItem,
    thunkAPI,
  ) => {
    try {
      const {
        item,
        error,
        msg,
      }: { item: ICartItemResponse; error: boolean; msg: string } =
        await cartService.create({
          user_id,
          product_id,
          product_variation_id,
          product_count,
        });
      return { item, error, msg };
    } catch (error: any) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return {
        item: null,
        error: true,
        msg: thunkAPI.rejectWithValue(message),
      };
    }
  },
);
// -------------------------------------------------------------------------------------------
// 장바구니 가져오기
export const getCartItems = createAsyncThunk(
  'cart/get',
  async (userId: string, thunkAPI) => {
    try {
      const {
        items,
        error,
        msg,
      }: { items: ICartItemResponse[] | []; error: boolean; msg: string } =
        await cartService.get(userId);
      return { items, error, msg };
    } catch (error: any) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return {
        items: [],
        error: true,
        msg: thunkAPI.rejectWithValue(message),
      };
    }
  },
);
// -------------------------------------------------------------------------------------------
// 장바구니 업데이트
export const updateCartItemAmount = createAsyncThunk(
  'cart/update',
  async (
    { id, product_count }: { id: number; product_count: number },
    thunkAPI,
  ) => {
    try {
      const {
        item,
        error,
        msg,
      }: { item: ICartItemResponse; error: boolean; msg: string } =
        await cartService.update({
          id,
          product_count,
        });
      return { item, error, msg };
    } catch (error: any) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return {
        item: null,
        error: true,
        msg: thunkAPI.rejectWithValue(message),
      };
    }
  },
);
// -------------------------------------------------------------------------------------------
// 장바구니 아이템 제거
export const deleteCartItem = createAsyncThunk(
  'cart/delete',
  async (id: number, thunkAPI) => {
    try {
      const { item, error, msg }: { item: {}; error: boolean; msg: string } =
        await cartService.remove(id);
      return { item: { id }, error, msg };
    } catch (error: any) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      return {
        item: { id },
        error: true,
        msg: thunkAPI.rejectWithValue(message),
      };
    }
  },
);

// ------------------------------------------ slice ------------------------------------------
export type CartInitType = {
  loading: boolean;
  error: boolean;
  errorMsg: any;
  items: ICartItemResponse[];
};

const initialState: CartInitType = {
  loading: false,
  error: false,
  errorMsg: '',
  items: [],
};
export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // 장바구니 생성 PENDING
    builder.addCase(createCartItem.pending, (state, action) => {
      state.loading = true;
      state.error = false;
    });
    // 장바구니 생성 FULFILLED
    builder.addCase(createCartItem.fulfilled, (state, action) => {
      const { item, msg, error } = action.payload;
      state.loading = false;
      state.error = error;
      state.errorMsg = '';
      if (error || item === null) {
        state.errorMsg = msg;
        return;
      }
    });
    // 장바구니 생성 REJECTED
    builder.addCase(createCartItem.rejected, (state, action) => {
      const { msg }: any = action.payload;
      state.loading = false;
      state.error = true;
      state.errorMsg = msg;
    });
    // 장바구니 가져오기 PENDING
    builder.addCase(getCartItems.pending, (state, action) => {
      state.loading = true;
      state.error = false;
    });
    // 장바구니 가져오기 FULFILLED
    builder.addCase(getCartItems.fulfilled, (state, action) => {
      const { items, msg, error } = action.payload;
      if (error) {
        state.loading = true;
        state.error = true;
        state.errorMsg = msg;
        return;
      }
      state.loading = false;
      state.error = false;
      state.items = items;
    });
    // 장바구니 가져오기 REJECTED
    builder.addCase(getCartItems.rejected, (state, action) => {
      const { msg }: any = action.payload;
      state.loading = false;
      state.error = true;
      state.errorMsg = msg;
    });

    // 장바구니 업데이트 FULFILLED
    builder.addCase(updateCartItemAmount.fulfilled, (state, action) => {
      const { item: responsedItem, msg, error } = action.payload;
      if (error) {
        state.loading = true;
        state.error = true;
        state.errorMsg = msg;
        return;
      }
      state.loading = false;
      state.error = false;
      if (responsedItem) {
        const idx = state.items.findIndex((v) => v.id === responsedItem.id);
        state.items[idx].product_count = responsedItem.product_count;
      }
    });

    // 장바구니 업데이트 REJECTED
    builder.addCase(updateCartItemAmount.rejected, (state, action) => {
      const { msg }: any = action.payload;
      state.loading = false;
      state.error = true;
      state.errorMsg = msg;
    });

    // 장바구니 아이템 제거 FULFILLED
    builder.addCase(deleteCartItem.fulfilled, (state, action) => {
      const {
        item: { id },
        error,
      } = action.payload;

      if (error) {
        state.loading = false;
        state.error = true;
        state.errorMsg = '장바구니 아이템 제거에 실패했어요 :(';
        return;
      }
      const idx = state.items.findIndex((v) => v.id === id);
      state.items.splice(idx, 1);
    });

    // 장바구니 아이템 제거 REJECTED
    builder.addCase(deleteCartItem.rejected, (state, action) => {
      const { msg }: any = action.payload;
      state.loading = false;
      state.error = true;
      state.errorMsg = msg;
    });
  },
});

export default cartSlice.reducer;
